import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AccessTimeFilled } from "@mui/icons-material";
import { Box, Stack, Typography, keyframes } from "@mui/material";
import dayjs from "dayjs";
import { useMedicalCareContext } from "@/contexts/MedicalCareContext";

const TIMER_FONT = '"Share Tech Mono", "Roboto Mono", ui-monospace, monospace';
const TIMER_WIDTH = 172;
const TIMER_HEIGHT = 44;

/** Posição inicial: topo central, dentro da faixa do header do app. */
function getInitialTimerPosition() {
  if (typeof window === "undefined") return { x: 24, y: 12 };
  return {
    x: Math.max(0, (window.innerWidth - TIMER_WIDTH) / 2),
    y: 12,
  };
}

const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(51, 190, 174, 0.4); }
  50% { box-shadow: 0 0 0 4px rgba(51, 190, 174, 0); }
`;

/** Formata milissegundos decorridos em HH:MM:SS. */
function formatElapsed(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return [hours, minutes, seconds]
    .map((unit) => String(unit).padStart(2, "0"))
    .join(":");
}

/**
 * Timer flutuante do atendimento.
 * Referência única: Encounter.actualPeriod.start (mesmo que a consulta tenha começado antes).
 */
export function AttendanceTimer() {
  const { encounter, loadingAppointment } = useMedicalCareContext();
  const [elapsedMs, setElapsedMs] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState(getInitialTimerPosition);

  const draggingRef = useRef(false);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const originMsRef = useRef<number | null>(null);

  // Carrega a fonte do timer uma única vez.
  useEffect(() => {
    const fontId = "attendance-timer-font";
    if (document.getElementById(fontId)) return;
    const link = document.createElement("link");
    link.id = fontId;
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Share+Tech+Mono&display=swap";
    document.head.appendChild(link);
  }, []);

  // Referência exclusiva: actualPeriod.start do Encounter.
  const encounterStart = encounter?.actualPeriod?.start;
  const originMs = useMemo(() => {
    if (!encounterStart) return null;
    const startMs = dayjs(encounterStart).valueOf();
    if (!Number.isFinite(startMs)) return null;
    return startMs;
  }, [encounterStart]);

  const formattedTime = formatElapsed(elapsedMs);
  const [hours, minutes, seconds] = formattedTime.split(":");

  useEffect(() => {
    if (originMs === null) return;
    originMsRef.current = originMs;
    setElapsedMs(Math.max(0, Date.now() - originMs));
  }, [originMs]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      const origin = originMsRef.current;
      if (origin === null) return;
      setElapsedMs(Math.max(0, Date.now() - origin));
    }, 1000);
    return () => window.clearInterval(intervalId);
  }, []);

  const clampPosition = useCallback((x: number, y: number) => {
    const maxX = Math.max(0, window.innerWidth - TIMER_WIDTH);
    const maxY = Math.max(0, window.innerHeight - TIMER_HEIGHT);
    return {
      x: Math.min(Math.max(0, x), maxX),
      y: Math.min(Math.max(0, y), maxY),
    };
  }, []);

  // Ao abrir o depurador (ou redimensionar a janela), mantém o timer visível.
  useEffect(() => {
    const keepVisible = () => {
      setPosition((prev) => clampPosition(prev.x, prev.y));
    };

    window.addEventListener("resize", keepVisible);
    window.visualViewport?.addEventListener("resize", keepVisible);

    return () => {
      window.removeEventListener("resize", keepVisible);
      window.visualViewport?.removeEventListener("resize", keepVisible);
    };
  }, [clampPosition]);

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    draggingRef.current = true;
    setIsDragging(true);
    dragOffsetRef.current = {
      x: event.clientX - position.x,
      y: event.clientY - position.y,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;
    setPosition(
      clampPosition(
        event.clientX - dragOffsetRef.current.x,
        event.clientY - dragOffsetRef.current.y
      )
    );
  };

  const handlePointerUp = () => {
    draggingRef.current = false;
    setIsDragging(false);
  };

  // Só exibe quando o Encounter tiver actualPeriod.start válido.
  if (loadingAppointment || originMs === null) return null;

  return createPortal(
    <Box
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      sx={{
        position: "fixed",
        left: position.x,
        top: position.y,
        zIndex: 1400,
        width: TIMER_WIDTH,
        height: TIMER_HEIGHT,
        px: 0.9,
        py: 0.55,
        borderRadius: "12px",
        cursor: isDragging ? "grabbing" : "grab",
        userSelect: "none",
        touchAction: "none",
        overflow: "hidden",
        background:
          "linear-gradient(145deg, rgba(255,255,255,0.96) 0%, rgba(230,247,245,0.92) 100%)",
        backdropFilter: "blur(14px)",
        border: "1px solid rgba(176, 230, 224, 0.9)",
        boxShadow: isDragging
          ? "0 10px 24px rgba(0, 124, 109, 0.24)"
          : "0 6px 18px rgba(0, 124, 109, 0.14), 0 1px 4px rgba(0, 0, 0, 0.05)",
        transition: "box-shadow 0.2s ease, transform 0.2s ease",
        transform: isDragging ? "scale(1.02)" : "scale(1)",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          borderRadius: "inherit",
          background:
            "linear-gradient(120deg, rgba(51,190,174,0.12) 0%, transparent 55%)",
          pointerEvents: "none",
        },
        "&:hover": {
          boxShadow:
            "0 8px 22px rgba(0, 124, 109, 0.2), 0 2px 8px rgba(0, 0, 0, 0.06)",
        },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.85} height="100%">
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            background:
              "linear-gradient(145deg, #33BEAE 0%, #007C6D 100%)",
            animation: `${pulseGlow} 2.4s ease-in-out infinite`,
          }}
        >
          <AccessTimeFilled
            sx={{
              fontSize: 18,
              color: "neutral0.main",
              filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.12))",
            }}
          />
        </Box>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={0.25}
          flex={1}
          minWidth={0}
        >
          {[hours, minutes, seconds].map((unit, index) => (
            <Stack
              key={`${unit}-${index}`}
              direction="row"
              alignItems="baseline"
              spacing={0.25}
            >
              {index > 0 && (
                <Typography
                  component="span"
                  sx={{
                    fontFamily: TIMER_FONT,
                    fontSize: 16,
                    fontWeight: 700,
                    color: "primary400.main",
                    lineHeight: 1,
                    opacity: 0.85,
                  }}
                >
                  :
                </Typography>
              )}
              <Typography
                component="span"
                sx={{
                  fontFamily: TIMER_FONT,
                  fontSize: 21,
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  color: "neutral900.main",
                  lineHeight: 1,
                  fontVariantNumeric: "tabular-nums",
                  WebkitTextStroke: "0.4px currentColor",
                  paintOrder: "stroke fill",
                }}
              >
                {unit}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Box>,
    document.body
  );
}
